import { Form, Spin, Tooltip, Typography } from "antd";
import { Alert } from "antd-v5";
import { FormInstance } from "antd/lib/form";
import AppLoader from "components/AppLoader";
import CustomCollapse from "components/CustomCollapse";
import { InfoIcon } from "components/Icons";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _ from "lodash";
import React, {
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";

import { CustomFieldV2, CustomFieldsV2 } from "../CustomFields/CustomFieldsV2";
import {
  CustomFieldStateMap,
  CustomFields,
  InitialValues,
} from "../CustomFields/types";
import {
  alterCustomFieldsAPIResponse,
  checkEmptyField,
  generateCustomFields,
} from "../CustomFields/utils";

type APIPramsRef = {
  [fieldName: string]: any;
};

type Props = {
  customFieldsValueRef: MutableRefObject<Map<string, any>>;
  useValueField?: boolean;
  form: FormInstance;
  loginConfigId: number;
  ticketConfigurationId?: number;
  initialValues: InitialValues;
  configId?: number;
  changeMode: boolean;
  innerRef?: boolean;
  toolName: string;
  tooltip?: string;
  showFieldDefaultValue?: boolean;
  runbook?: boolean;
  onLoadingChange?: (loading: boolean) => void;
};

const AsanaCustomFields = forwardRef(
  (
    {
      customFieldsValueRef,
      useValueField = false,
      form,
      loginConfigId,
      ticketConfigurationId,
      initialValues,
      configId,
      changeMode,
      innerRef = false,
      toolName,
      tooltip,
      showFieldDefaultValue = true,
      runbook = false,
      onLoadingChange,
    }: Props,
    ref,
  ) => {
    const initialData = useRef(initialValues);
    const [inputCustomFields, setInputCustomFields] = useState<CustomFields>(
      [],
    );
    const customFieldStateRef = useRef<CustomFieldStateMap>({});
    const [loading, setLoading] = useState(false);
    const apiParamRef = useRef<APIPramsRef>({});
    const thisRef = useRef(innerRef);
    const featureFlags = useFeatureFlag();

    const text = useMemo(() => {
      if (loading) {
        return;
      }
      let message = "No Custom Fields.";
      if (
        !loginConfigId ||
        !initialValues.organisation ||
        !initialValues.issueType
      ) {
        message = "Please select Workspace and Issue Type";
      }
      return <Typography.Text strong>{message}</Typography.Text>;
    }, [loginConfigId, initialValues, loading]);

    const resetFields = useCallback(() => {
      setInputCustomFields([]);
      customFieldStateRef.current = {};
      apiParamRef.current = {};
    }, []);

    const onValuesChange = useCallback(
      (current: Record<string, any>) => {
        if (customFieldsValueRef) {
          customFieldsValueRef.current = {
            ...customFieldsValueRef.current,
            ...current,
          };
        }
        _.forEach(current, (value, key) => {
          const newState = {
            ...customFieldStateRef.current,
            [key]: _.isBoolean(value) ? value : !_.isEmpty(value),
          };
          customFieldStateRef.current = newState;
        });
      },
      [customFieldsValueRef],
    );

    useEffect(() => {
      onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
      if (!checkEmptyField(loginConfigId)) {
        const { organisation, issueType, issueTypeId } = initialValues;
        if (organisation && issueType) {
          setInputCustomFields([]);
          setLoading(true);
          let isEdit = changeMode;
          if (isEdit && !thisRef.current) {
            thisRef.current = true;
            isEdit = false;
          }
          if (!issueType) {
            return;
          }
          // pass configId if fetching existing custom fields config
          // pass loginConfigId, issueType and organisation if issueType/organisation is changed or not in edit mode
          const params =
            !configId ||
            (isEdit &&
              (issueType !== initialData.current?.issueType ||
                organisation !== initialData.current?.organisation))
              ? {
                  loginConfigId,
                  organisation,
                  issueType,
                  ...(issueTypeId ? { issueTypeId } : {}),
                }
              : { configId };
          const { promise, cancel } =
            TicketsCreationAPI.getCustomTicketFields(params);
          promise
            .then(({ fields }: { fields: CustomFields }) => {
              fields.forEach((field) => {
                if (field.key === "__ac_asana_projects") {
                  field.isRequired = true;
                  field.disableVariableSelection = true;
                }
              });
              const { customState, fields: modifiedFields } =
                alterCustomFieldsAPIResponse({
                  fields,
                  form,
                  initialData,
                  useValueField,
                  customFieldsValueRef,
                  featureFlags,
                  innerRef,
                  toolName,
                  projectKey: organisation,
                  showFieldDefaultValue,
                  skipDefaultValue: true,
                });
              customFieldStateRef.current = customState;
              setInputCustomFields(modifiedFields);
            })
            .finally(() => setLoading(false));
          return cancel;
        } else {
          resetFields();
        }
      }
    }, [
      customFieldsValueRef,
      loginConfigId,
      ticketConfigurationId,
      form,
      initialValues,
      setInputCustomFields,
      apiParamRef,
      changeMode,
      configId,
      resetFields,
      innerRef,
      useValueField,
      toolName,
      featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
      featureFlags,
      showFieldDefaultValue,
    ]);

    // For Asana, the second param is organisation (not projectKey)
    // The getApiParams callback builds params with organisation key for field suggestion APIs
    const getApiParams = useCallback(
      (
        loginConfigId: number,
        organisationValue: string,
        fieldLabel: string,
        type?: string,
      ) => {
        const newApiParams = {
          loginConfigId,
          organisation: organisationValue,
          issueType: initialValues.issueType,
          fieldLabel,
          type,
        };
        if (!_.isEqual(apiParamRef.current[fieldLabel], newApiParams)) {
          apiParamRef.current[fieldLabel] = newApiParams;
        }
        return apiParamRef.current[fieldLabel];
      },
      [initialValues.issueType],
    );

    const customField: Array<CustomFieldV2> = useMemo(() => {
      return generateCustomFields({
        fields: inputCustomFields,
        projectKey: initialValues.organisation,
        loginConfigId,
        initialValues,
        featureFlags,
        customFieldStateRef,
        getApiParams,
      });
    }, [
      inputCustomFields,
      initialValues,
      loginConfigId,
      featureFlags,
      getApiParams,
    ]);

    const renderFields = useCallback(
      (customFields: CustomFieldV2[]) => {
        return (
          <CustomFieldsV2
            customFields={customFields}
            form={form}
            onValuesChange={onValuesChange}
            toolName={toolName}
            runbook={runbook}
            initialValues={initialData.current}
            customFieldsValueRef={customFieldsValueRef}
          />
        );
      },
      [form, onValuesChange, toolName, runbook, customFieldsValueRef],
    );

    useImperativeHandle(ref, () => ({
      getCustomFields: () => ({
        inputCustomFields,
        customFieldState: customFieldStateRef.current,
      }),
    }));

    const collapseProps: any = {
      type: "info",
      Key: "custom_field",
      defaultActiveKey: "custom_field",
      props: {
        destroyInactivePanel: true,
      },
      header: (
        <>
          Configure Custom Fields{" "}
          <Tooltip
            title={
              tooltip ||
              "By enabling the toggles, only those fields will be mapped to your project and the rest will be ignored. A text area and drop-down boxes allow you to add default values. These values will be considered every time you create a ticket."
            }
          >
            {" "}
            <InfoIcon />
          </Tooltip>
          <div className="m-t-m">
            <Alert
              description="Projects and custom fields are refreshed every 24 hours. If you recently added or modified projects or custom fields in Asana, changes may take up to 24 hours to appear here."
              type="info"
              showIcon
            />
          </div>
        </>
      ),
      description: (
        <Form
          onValuesChange={onValuesChange}
          form={form}
          layout="vertical"
          initialValues={initialData.current}
        >
          <Spin
            spinning={loading}
            indicator={<AppLoader style={{ marginTop: 0 }} />}
          >
            {_.isEmpty(customField) ? text : renderFields(customField)}
          </Spin>
        </Form>
      ),
      isCollapseDisabled: customField.some((field) => field.requireField),
    };

    return <CustomCollapse {...collapseProps} />;
  },
);

export default AsanaCustomFields;
